import { useRef, useMemo } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'

/**
 * Chapter 7: Silent Vibrations
 * Cymatics-inspired circular plate with wave pattern displacement
 * Optimized: reduced vertex count from 128x128 (16K) to 64x64 (4K)
 * Pre-cached distance/angle calculations
 */
export default function CymaticsPlate({ audioData, isActive, position = [0, 0, 0] }) {
  const plateRef = useRef()
  const ringsRef = useRef([])

  const { fft, bass, mids, highs, amplitude, beat } = audioData || {
    fft: new Float32Array(128).fill(0),
    bass: 0,
    mids: 0,
    highs: 0,
    amplitude: 0,
    beat: 0
  }

  // Reduced resolution: 64x64 = 4,096 vertices (was 128x128 = 16,384)
  const planeGeometry = useMemo(() => {
    const geometry = new THREE.PlaneGeometry(4, 4, 64, 64)
    geometry.rotateX(-Math.PI / 2)
    return geometry
  }, [])

  // Pre-cache distance and angle for each vertex (computed once, not per frame)
  const vertexCache = useMemo(() => {
    const positions = planeGeometry.attributes.position.array
    const count = planeGeometry.attributes.position.count
    const distances = new Float32Array(count)
    const angles = new Float32Array(count)
    const fftIndices = new Uint16Array(count)

    for (let i = 0; i < count; i++) {
      const x = positions[i * 3]
      const z = positions[i * 3 + 2]
      distances[i] = Math.sqrt(x * x + z * z)
      angles[i] = Math.atan2(z, x)
      fftIndices[i] = Math.min(Math.floor((distances[i] / 2) * fft.length), fft.length - 1)
    }

    return { distances, angles, fftIndices, count }
  }, [planeGeometry])

  // Create concentric rings
  const rings = useMemo(() => {
    return Array.from({ length: 8 }, (_, i) => ({
      id: i,
      radius: 0.3 + i * 0.25,
      thickness: 0.03
    }))
  }, [])

  useFrame((state, delta) => {
    if (!isActive || !plateRef.current) return

    const time = state.clock.elapsedTime
    const positions = plateRef.current.geometry.attributes.position
    const { distances, angles, fftIndices, count } = vertexCache

    // Apply cymatics wave patterns using cached values
    for (let i = 0; i < count; i++) {
      const d = distances[i]
      const a = angles[i]

      const wave1 = Math.sin(d * 3 - time * 0.8) * bass * 0.5
      const wave2 = Math.sin(d * 5 + time * 0.6) * mids * 0.3
      const wave3 = Math.sin(d * 8 - time * 1.2) * highs * 0.2
      const radialWave = Math.sin(a * 6 + time) * amplitude * 0.1
      const fftDisplacement = (fft[fftIndices[i]] || 0) * 0.3

      positions.array[i * 3 + 1] = wave1 + wave2 + wave3 + radialWave + fftDisplacement
    }

    positions.needsUpdate = true

    // Rotate plate slowly
    plateRef.current.rotation.y += delta * 0.1

    // Pulse concentric rings
    ringsRef.current.forEach((ring, index) => {
      if (!ring) return

      let scale = 1
      if (index < 3) {
        scale = 1 + bass * 0.1
      } else if (index < 6) {
        scale = 1 + mids * 0.1
      } else {
        scale = 1 + highs * 0.1
      }

      ring.scale.setScalar(scale)

      if (ring.material) {
        ring.material.opacity = 0.3 + (beat > 0.5 ? 0.5 : 0) + amplitude * 0.2
      }

      ring.rotation.z += delta * (0.1 + index * 0.02)
    })
  })

  return (
    <group position={position}>
      {/* Main cymatics plate */}
      <mesh ref={plateRef} geometry={planeGeometry}>
        <meshStandardMaterial
          color="#1E90FF"
          emissive="#00BFFF"
          emissiveIntensity={0.3}
          metalness={0.6}
          roughness={0.4}
          wireframe={false}
          side={THREE.DoubleSide}
        />
      </mesh>

      {/* Wireframe overlay for detail */}
      <mesh geometry={planeGeometry} rotation={[0, 0, 0]}>
        <meshBasicMaterial
          color="#00BFFF"
          wireframe
          transparent
          opacity={0.2}
        />
      </mesh>

      {/* Concentric ring patterns - reduced segment count */}
      {rings.map((ring, index) => (
        <mesh
          key={ring.id}
          ref={(el) => (ringsRef.current[index] = el)}
          rotation={[-Math.PI / 2, 0, 0]}
        >
          <ringGeometry args={[ring.radius, ring.radius + ring.thickness, 32]} />
          <meshBasicMaterial
            color="#00BFFF"
            transparent
            opacity={0.3}
            side={THREE.DoubleSide}
          />
        </mesh>
      ))}

      {/* Shadow rings at different height */}
      {rings.map((ring) => (
        <mesh
          key={`ring-shadow-${ring.id}`}
          position={[0, -0.1, 0]}
          rotation={[-Math.PI / 2, 0, 0]}
        >
          <ringGeometry args={[ring.radius, ring.radius + ring.thickness, 32]} />
          <meshBasicMaterial
            color="#0066CC"
            transparent
            opacity={0.1}
            side={THREE.DoubleSide}
          />
        </mesh>
      ))}

      {/* Center glow point */}
      <mesh position={[0, 0.1, 0]}>
        <sphereGeometry args={[0.15, 16, 16]} />
        <meshStandardMaterial
          color="#FFFFFF"
          emissive="#00BFFF"
          emissiveIntensity={2}
          transparent
          opacity={0.8}
        />
      </mesh>

      {/* Lighting */}
      <pointLight position={[0, 2, 0]} intensity={2} color="#00BFFF" distance={8} />
      <pointLight position={[0, -1, 0]} intensity={1} color="#1E90FF" distance={6} />
    </group>
  )
}
